import { NAV_FOOTER, NAV_PREMIER_TIRAGE, NAV_RUBRIQUES, type NavItem } from "../helpers/navCatalog.generated";

export type NavSearchType = "numero" | "date" | "mot" | "sans_resultat";
export interface NavSuggestion { label: string; href: string; hint?: string }
export interface NavSearchResult { type: NavSearchType; suggestions: NavSuggestion[] }

export const SANS_RESULTAT_MESSAGE = "Tapez un numéro (1 à 56) ou une date";

export const FALLBACK_SUGGESTIONS: NavSuggestion[] = [
  { label: "Voir le dernier tirage", href: "/tirage-du-jour/" },
  { label: "Voir les 56 numéros", href: "/keno-statistiques/" },
  { label: "Parcourir les derniers tirages", href: "/derniers-tirages-keno/" },
];

/** Résultat officiel disponible à partir de 20 h 15, heure de Paris. */
export const RESULTAT_DISPONIBLE_MINUTES = 20 * 60 + 15;

const MOIS = ["janvier", "fevrier", "mars", "avril", "mai", "juin", "juillet", "aout", "septembre", "octobre", "novembre", "decembre"];
const SANS_RESULTAT: NavSearchResult = { type: "sans_resultat", suggestions: FALLBACK_SUGGESTIONS };

/** Pages proposées par la recherche : rubriques, colonnes KenoMetric et Légal (pas les routes de l'application). */
export function searchCatalog(): NavItem[] {
  return [...NAV_RUBRIQUES.flatMap((r) => r.items), ...NAV_FOOTER.kenometric, ...NAV_FOOTER.legal];
}

export function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim();
}

/** Date du jour (AAAA-MM-JJ) et minutes depuis minuit, à l'heure de Paris. */
export function parisNow(now: Date): { iso: string; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return { iso: `${get("year")}-${get("month")}-${get("day")}`, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
}

const pad = (n: number) => String(n).padStart(2, "0");
const toIso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

function isRealDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1) return false;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

function moisDepuis(mot: string): number | null {
  const exact = MOIS.indexOf(mot);
  if (exact >= 0) return exact + 1;
  if (mot.length < 3) return null;
  const candidats = MOIS.filter((m) => m.startsWith(mot));
  return candidats.length === 1 ? MOIS.indexOf(candidats[0]) + 1 : null;
}

function parseDate(q: string): { d: number; m: number; y: number | null } | null {
  let match = q.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };

  match = q.match(/^(\d{1,2})\s*[/.-]\s*(\d{1,2})(?:\s*[/.-]\s*(\d{2}|\d{4}))?$/);
  if (match) {
    const y = match[3] === undefined ? null : match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
    return { d: Number(match[1]), m: Number(match[2]), y };
  }

  match = q.match(/^(?:le )?(\d{1,2})(?:er)? ([a-z]+)\.?(?: (\d{4}))?$/);
  if (match) {
    const m = moisDepuis(match[2]);
    if (m === null) return null;
    return { d: Number(match[1]), m, y: match[3] === undefined ? null : Number(match[3]) };
  }
  return null;
}

function libelleDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const texte = new Intl.DateTimeFormat("fr-FR", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" })
    .format(new Date(Date.UTC(y, m - 1, d)));
  return `Tirage du ${texte}`;
}

function resolveDate(parsed: { d: number; m: number; y: number | null }, today: Date, premierTirage: string): NavSearchResult {
  const paris = parisNow(today);
  const anneeCourante = Number(paris.iso.slice(0, 4));
  let y = parsed.y ?? anneeCourante;
  if (parsed.y === null && isRealDate(y, parsed.m, parsed.d) && toIso(y, parsed.m, parsed.d) > paris.iso) {
    y -= 1;
  }
  if (!isRealDate(y, parsed.m, parsed.d)) return SANS_RESULTAT;

  const iso = toIso(y, parsed.m, parsed.d);
  if (iso > paris.iso || iso < premierTirage) return SANS_RESULTAT;
  if (iso === paris.iso && paris.minutes < RESULTAT_DISPONIBLE_MINUTES) {
    return { type: "date", suggestions: [{ label: "Tirage de ce soir à 20 h : voir le prochain tirage", href: "/prochain-tirage/" }] };
  }
  return { type: "date", suggestions: [{ label: libelleDate(iso), href: `/tirage/${iso}/` }] };
}

function hint(tier: NavItem["tier"]): string | undefined {
  if (tier === "account") return "Compte gratuit";
  if (tier === "premium") return "Premium";
  return undefined;
}

export function resolveNavQuery(query: string, today: Date, items: NavItem[], premierTirage: string = NAV_PREMIER_TIRAGE): NavSearchResult {
  const q = normalize(query);

  const numero = q.match(/^(?:(?:n|no|n°|num|numero)\s*\.?\s*|#)?(\d{1,3})$/);
  if (numero) {
    const n = Number(numero[1]);
    return n >= 1 && n <= 56
      ? { type: "numero", suggestions: [{ label: `Fiche du numéro ${n}`, href: `/numero/${n}/` }] }
      : SANS_RESULTAT;
  }

  const date = parseDate(q);
  if (date) return resolveDate(date, today, premierTirage);
  if (/^[\d\s/.-]+$/.test(q) && q !== "") return SANS_RESULTAT;

  if (q.length < 2) return { type: "mot", suggestions: [] };

  const mots = q.split(" ");
  const scored = items
    .map((item) => {
      const label = normalize(item.label);
      const texte = `${label} ${normalize(item.desc)}`;
      if (!mots.every((mot) => texte.includes(mot))) return null;
      const score = label.startsWith(q) ? 0 : mots.every((mot) => label.includes(mot)) ? 1 : 2;
      return { item, score };
    })
    .filter((x): x is { item: NavItem; score: number } => x !== null)
    .sort((a, b) => a.score - b.score);

  if (scored.length === 0) return SANS_RESULTAT;
  return {
    type: "mot",
    suggestions: scored.slice(0, 5).map(({ item }) => {
      const h = hint(item.tier);
      return h ? { label: item.label, href: item.href, hint: h } : { label: item.label, href: item.href };
    }),
  };
}
